import {
  Box,
  Card,
  Circle,
  Collapsible,
  Flex,
  Icon,
  IconButton,
  Image,
  Stack,
  Text,
} from '@chakra-ui/react';
import { memo, useCallback, useMemo } from 'react';
import { LuChevronDown, LuChevronUp, LuCircleDashed, LuUser } from 'react-icons/lu';

import ShareButton from '@components/common/ShareButton';
import AISummary from '@features/positions/hedgePositions/AISummary';
import ContractDetail from '@features/positions/hedgePositions/ContractDetail';
import { allCommodities, Contract, ContractPosition, ContractStatus } from 'helpers/fakeData';
import { useBoolean } from 'hooks/useBoolean';
import { useHref } from 'hooks/useHref';
import { CalendarIcon } from 'vectors';

const statusConfig: Record<ContractStatus, { label: string; color: string; dotColor: string }> = {
  PENDING: { label: 'Pending', color: 'text.primary', dotColor: 'gray.400' },
  ACTIVE: { label: 'Active', color: 'text.primary', dotColor: 'green.400' },
  EXPIRED: { label: 'Expired', color: 'text.primary', dotColor: 'red.400' },
};

const StatusIcon = ({ status }: { status: ContractStatus }) => {
  if (status === 'PENDING') {
    return <Box as={LuCircleDashed} boxSize="18px" color="text.secondary" />;
  }
  return <Circle size="16px" bg={statusConfig[status].dotColor} />;
};

const getPositionLabel = (position: ContractPosition) => {
  if (position === 'BUYER') {
    return 'Buying';
  }
  return 'Selling';
};
const PositionBadge = ({ position }: { position: ContractPosition | undefined }) => {
  if (position == null) {
    return null;
  }
  return (
    <Flex
      bg="surface.hover"
      borderWidth="1px"
      borderColor="border.light"
      borderRadius="md"
      px={2}
      py={0.5}
      alignItems="center"
    >
      <Text
        fontSize="xs"
        fontWeight="semibold"
        color={position === 'BUYER' ? 'green.600' : 'red.600'}
        letterSpacing="wide"
      >
        {getPositionLabel(position).toUpperCase()}
      </Text>
    </Flex>
  );
};

const CreatorAvatar = () => {
  return (
    <Circle size="24px" bg="orange.100">
      <Box as={LuUser} boxSize="14px" color="orange.600" />
    </Circle>
  );
};

interface Props {
  contract: Contract;
  defaultOpen?: boolean;
  hideCommodityLogo?: boolean;
}

const ContractCard = ({ contract, defaultOpen = false, hideCommodityLogo = false }: Props) => {
  const config = statusConfig[contract.status];
  const [open, setOpen] = useBoolean(defaultOpen);
  const commodity = allCommodities.find((c) => c.id === contract.commodityId);
  const { contractHref } = useHref();

  const handleOpenChange = useCallback(() => {
    setOpen.toggle();
  }, [setOpen]);

  const title = useMemo(() => {
    return `${commodity?.name} for GHS${contract.forwardPrice} per kg`;
  }, []);

  if (commodity == null) {
    return null;
  }

  const href = contractHref(contract.id);
  return (
    <Card.Root
      w="full"
      borderColor="border.light"
      borderRadius="xl"
      overflow="hidden"
      bg="surface.background"
    >
      <Card.Body p={0} gap={3}>
        <Flex direction="row" alignItems="center" gap={2} px={5} pt={5}>
          <StatusIcon status={contract.status} />
          <Text fontSize="sm" fontWeight="semibold" color={config.color}>
            {config.label}
          </Text>
          <ShareButton ml="auto" ariaLabel="Share contract" size="xs" variant="plain" href={href} />
        </Flex>
        <Flex flexDir="column" gap={3} px={5} fontWeight="medium">
          <Flex
            direction="row"
            alignItems="center"
            gap={2}
            onClick={setOpen.toggle}
            cursor="pointer"
          >
            <PositionBadge position={contract.position} />
            {!hideCommodityLogo && (
              <Image src={commodity.image} alt={commodity.name} boxSize="32px" />
            )}
            <Text fontSize="lg">{title}</Text>
          </Flex>
          <Stack
            direction={{ base: 'column', md: 'row' }}
            gap={3}
            alignItems={{ base: 'flex-start', md: 'center' }}
            fontWeight="semibold"
          >
            <Flex direction="row" alignItems="center" gap={2} mt={1}>
              <Text fontSize="xs" color="text.secondary">
                Quantity
              </Text>
              <Text fontSize="sm">{contract.contractSize}kg</Text>
            </Flex>
            <Flex direction="row" alignItems="center" gap={2} mt={1}>
              <Text fontSize="xs" color="text.secondary">
                Settlement Date
              </Text>
              <CalendarIcon boxSize="14px" />
              <Text fontSize="sm">{contract.settlementDate}</Text>
            </Flex>
            <Flex direction="row" alignItems="center" gap={2} mt={1}>
              <Text fontSize="xs" color="text.secondary">
                Created By
              </Text>
              <CreatorAvatar />
              <Text fontSize="sm">{contract.createdBy}</Text>
            </Flex>
          </Stack>
        </Flex>
        <Collapsible.Root open={open} onOpenChange={handleOpenChange} lazyMount unmountOnExit>
          <Flex
            flexDir="column"
            w="full"
            borderTopColor="border.light"
            borderTopWidth="1px"
            px={5}
            py={1}
            bg="surface.muted"
          >
            <Collapsible.Trigger asChild>
              <Flex
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                cursor="pointer"
              >
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color="text.secondary"
                  opacity={open ? 0 : 1}
                  transition="opacity 0.2s"
                >
                  AI summary & more
                </Text>
                <IconButton
                  variant="ghost"
                  size="sm"
                  fontWeight="semibold"
                  color="text.secondary"
                  px={2}
                  borderRadius="md"
                  ml="auto"
                >
                  {open ? 'Hide details' : 'Show details'}
                  <Icon>{open ? <LuChevronUp /> : <LuChevronDown />}</Icon>
                </IconButton>
              </Flex>
            </Collapsible.Trigger>
          </Flex>
          <Collapsible.Content p={0}>
            <Flex direction="column" gap={4} px={5} pb={5} bg="surface.muted">
              <AISummary />
              <ContractDetail contract={contract} />
            </Flex>
          </Collapsible.Content>
        </Collapsible.Root>
      </Card.Body>
    </Card.Root>
  );
};

export default memo(ContractCard);
